import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Package, 
  Plus, 
  Clock, 
  CheckCircle,
  Calendar,
  RotateCcw,
  RefreshCw,
  AlertCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyRentals = () => {
  const navigate = useNavigate();
  const [rentals, setRentals] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);
  const [returnRequests, setReturnRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRequestOpen, setIsRequestOpen] = useState(false);
  const [returnDialog, setReturnDialog] = useState({ open: false, rental: null, type: "return" });
  const [returnNotes, setReturnNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [requestData, setRequestData] = useState({
    item_id: "",
    quantity: 1,
    requested_start_date: new Date().toISOString().split('T')[0],
    notes: "",
  });

  const fetchData = async () => {
    try {
      const [rentalsRes, itemsRes, requestsRes] = await Promise.all([
        axios.get(`${API}/my/rentals`),
        axios.get(`${API}/rental-items/available`),
        axios.get(`${API}/rental-return-requests`),
      ]);
      setRentals(rentalsRes.data);
      setAvailableItems(itemsRes.data);
      setReturnRequests(requestsRes.data);
    } catch (error) {
      toast.error("Failed to load rental data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRequestRental = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/rentals/request`, requestData);
      toast.success("Rental request submitted successfully");
      setIsRequestOpen(false);
      setRequestData({
        item_id: "",
        quantity: 1,
        requested_start_date: new Date().toISOString().split('T')[0],
        notes: "",
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit rental request");
    }
  };

  const handleReturnRequest = async () => {
    if (!returnDialog.rental) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/rental-return-requests`, {
        rental_id: returnDialog.rental.rental_id,
        request_type: returnDialog.type,
        customer_notes: returnNotes,
      });
      toast.success(
        returnDialog.type === "return"
          ? "Return request submitted"
          : "Replacement request submitted"
      );
      setReturnDialog({ open: false, rental: null, type: "return" });
      setReturnNotes("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: "bg-green-100 text-green-800",
      reserved: "bg-slate-100 text-slate-800",
      pending_delivery: "bg-cyan-100 text-cyan-800",
      delivered: "bg-blue-100 text-blue-800",
      return_scheduled: "bg-purple-100 text-purple-800",
      returned: "bg-slate-200 text-slate-700",
      overdue: "bg-red-100 text-red-800",
      pending_approval: "bg-yellow-100 text-yellow-800",
      completed: "bg-emerald-100 text-emerald-800",
      cancelled: "bg-red-50 text-red-600",
      declined: "bg-red-50 text-red-600",
    };
    return (
      <Badge className={`rounded-sm text-xs ${styles[status] || "bg-slate-100"}`}>
        {status?.replace(/_/g, " ")}
      </Badge>
    );
  };

  const canRequestReturn = (rental) => {
    // Only delivered rentals can be returned/replaced
    if (rental.status !== "delivered") return false;
    // Check if there's already a pending request for this rental
    return !returnRequests.some(r => r.rental_id === rental.rental_id && r.status === "pending");
  };

  const activeRentals = rentals.filter(r => !["returned", "completed", "declined", "cancelled"].includes(r.status));
  const pastRentals = rentals.filter(r => ["returned", "completed", "declined", "cancelled"].includes(r.status));
  const pendingRequests = returnRequests.filter(r => r.status === "pending");
  const processedRequests = returnRequests.filter(r => r.status !== "pending");

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="my-rentals-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">My Rentals</h1>
          <p className="text-sm text-slate-500 mt-1">View and manage your rental items</p>
        </div>
        <Button onClick={() => setIsRequestOpen(true)} data-testid="request-rental-btn">
          <Plus className="h-4 w-4 mr-2" />
          Request Rental
        </Button>
      </div>

      {/* Pending Requests Alert */}
      {pendingRequests.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-amber-800">
              <Clock className="h-4 w-4" />
              Pending Requests ({pendingRequests.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pendingRequests.map((req) => (
                <div key={req.request_id} className="flex items-center justify-between bg-white p-3 rounded-sm border">
                  <div>
                    <div className="flex items-center gap-2">
                      {req.request_type === "replacement" ? (
                        <RefreshCw className="h-4 w-4 text-purple-500" />
                      ) : (
                        <RotateCcw className="h-4 w-4 text-blue-500" />
                      )}
                      <span className="font-medium text-sm">
                        {req.request_type === "replacement" ? "Replacement" : "Return"} Request
                      </span>
                      <span className="text-slate-400">—</span>
                      <span className="text-sm text-slate-600">{req.item_name}</span>
                    </div>
                    {req.customer_notes && (
                      <p className="text-xs text-slate-500 mt-1 ml-6">{req.customer_notes}</p>
                    )}
                  </div>
                  <Badge className="bg-amber-100 text-amber-800 rounded-sm text-xs">Awaiting Approval</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Approved/Processed Requests */}
      {processedRequests.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-slate-500" />
              Request Updates
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {processedRequests.slice(0, 5).map((req) => (
                <div key={req.request_id} className="flex items-center justify-between bg-slate-50 p-3 rounded-sm">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">
                        {req.request_type === "replacement" ? "Replacement" : "Return"} — {req.item_name}
                      </span>
                    </div>
                    {req.status === "approved" && (
                      <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                        {req.collection_date && <p>📦 Collection: {req.collection_date}</p>}
                        {req.delivery_date && <p>🚚 Delivery: {req.delivery_date}</p>}
                        {req.replacement_item_name && <p>🔄 Replacement: {req.replacement_item_name}</p>}
                        {req.admin_notes && <p>📝 {req.admin_notes}</p>}
                      </div>
                    )}
                  </div>
                  <Badge className={`rounded-sm text-xs ${
                    req.status === "approved" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"
                  }`}>
                    {req.status === "approved" ? "Approved" : "Declined"}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Rentals */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-green-600" />
              Active Rentals ({activeRentals.length})
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {activeRentals.length > 0 ? (
            <>
              {/* Mobile Cards */}
              <div className="md:hidden space-y-3">
                {activeRentals.map((rental) => (
                  <div 
                    key={rental.rental_id} 
                    className="border rounded-sm p-4 bg-green-50/50 cursor-pointer hover:bg-green-50"
                    onClick={() => navigate(`/rental-job-card/${rental.rental_id}`)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium">{rental.item_name}</h3>
                      {getStatusBadge(rental.status)}
                    </div>
                    <div className="text-sm text-slate-500 space-y-1">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        Since: {rental.start_date}
                      </div>
                      {rental.expected_return_date && (
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          Expected return: {rental.expected_return_date}
                        </div>
                      )}
                    </div>
                    {canRequestReturn(rental) && (
                      <div className="flex gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setReturnDialog({ open: true, rental, type: "return" });
                            setReturnNotes("");
                          }}
                          className="rounded-sm text-xs"
                        >
                          <RotateCcw className="h-3 w-3 mr-1" /> Return
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setReturnDialog({ open: true, rental, type: "replacement" });
                            setReturnNotes("");
                          }}
                          className="rounded-sm text-xs"
                        >
                          <RefreshCw className="h-3 w-3 mr-1" /> Replace
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {/* Desktop Table */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Rental ID</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead>Qty</TableHead>
                      <TableHead>Start Date</TableHead>
                      <TableHead>Expected Return</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeRentals.map((rental) => (
                      <TableRow 
                        key={rental.rental_id}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => navigate(`/rental-job-card/${rental.rental_id}`)}
                      >
                        <TableCell>
                          <code className="text-xs bg-slate-100 px-2 py-0.5 rounded">{rental.rental_id}</code>
                        </TableCell>
                        <TableCell className="font-medium">{rental.item_name}</TableCell>
                        <TableCell>{rental.quantity}</TableCell>
                        <TableCell>{rental.start_date}</TableCell>
                        <TableCell>{rental.expected_return_date || "—"}</TableCell>
                        <TableCell>{getStatusBadge(rental.status)}</TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          {canRequestReturn(rental) && (
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setReturnDialog({ open: true, rental, type: "return" });
                                  setReturnNotes("");
                                }}
                                className="rounded-sm text-xs"
                              >
                                <RotateCcw className="h-3 w-3 mr-1" /> Return
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setReturnDialog({ open: true, rental, type: "replacement" });
                                  setReturnNotes("");
                                }}
                                className="rounded-sm text-xs"
                              >
                                <RefreshCw className="h-3 w-3 mr-1" /> Replace
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No active rentals</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Past Rentals */}
      {pastRentals.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-slate-400" />
              Past Rentals ({pastRentals.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rental ID</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>Qty</TableHead>
                    <TableHead>Start Date</TableHead>
                    <TableHead>Return Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pastRentals.map((rental) => (
                    <TableRow 
                      key={rental.rental_id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => navigate(`/rental-job-card/${rental.rental_id}`)}
                    >
                      <TableCell>
                        <code className="text-xs bg-slate-100 px-2 py-0.5 rounded">{rental.rental_id}</code>
                      </TableCell>
                      <TableCell className="font-medium">{rental.item_name}</TableCell>
                      <TableCell>{rental.quantity}</TableCell>
                      <TableCell>{rental.start_date}</TableCell>
                      <TableCell>{rental.actual_return_date || rental.expected_return_date || "—"}</TableCell>
                      <TableCell>{getStatusBadge(rental.status)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="md:hidden space-y-3">
              {pastRentals.map((rental) => (
                <div 
                  key={rental.rental_id}
                  className="border rounded-sm p-3 cursor-pointer hover:bg-slate-50"
                  onClick={() => navigate(`/rental-job-card/${rental.rental_id}`)}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{rental.item_name}</span>
                    {getStatusBadge(rental.status)}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Returned: {rental.actual_return_date || rental.expected_return_date}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Request Rental Dialog */}
      <Dialog open={isRequestOpen} onOpenChange={setIsRequestOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request a Rental</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRequestRental} className="space-y-4">
            <div>
              <Label>Rental Item *</Label>
              <Select
                value={requestData.item_id}
                onValueChange={(value) => setRequestData({ ...requestData, item_id: value })}
              >
                <SelectTrigger data-testid="request-item-select">
                  <SelectValue placeholder="Select item" />
                </SelectTrigger>
                <SelectContent>
                  {availableItems.map((item) => (
                    <SelectItem key={item.item_id} value={item.item_id}>
                      {item.name} ({item.available_quantity} available)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantity</Label>
                <Input
                  type="number"
                  min="1"
                  value={requestData.quantity}
                  onChange={(e) => setRequestData({ ...requestData, quantity: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div>
                <Label>Requested Start Date</Label>
                <Input
                  type="date"
                  value={requestData.requested_start_date}
                  onChange={(e) => setRequestData({ ...requestData, requested_start_date: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={requestData.notes}
                onChange={(e) => setRequestData({ ...requestData, notes: e.target.value })}
                placeholder="Any special requirements..."
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setIsRequestOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">Submit Request</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Return/Replacement Request Dialog */}
      <Dialog open={returnDialog.open} onOpenChange={(open) => !open && setReturnDialog({ open: false, rental: null, type: "return" })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {returnDialog.type === "replacement" ? (
                <><RefreshCw className="h-5 w-5 text-purple-500" /> Request Replacement</>
              ) : (
                <><RotateCcw className="h-5 w-5 text-blue-500" /> Request Return</>
              )}
            </DialogTitle>
          </DialogHeader>
          {returnDialog.rental && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-sm p-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Item</span>
                  <span className="font-medium">{returnDialog.rental.item_name}</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-slate-500">Rental ID</span>
                  <code className="text-xs bg-white px-2 py-0.5 rounded">{returnDialog.rental.rental_id}</code>
                </div>
              </div>
              {returnDialog.type === "replacement" && (
                <div className="bg-purple-50 rounded-sm p-3 text-sm">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-purple-600 mt-0.5 shrink-0" />
                    <p className="text-purple-700">
                      A replacement item will be assigned by the admin. You'll receive a delivery date once approved.
                    </p>
                  </div>
                </div>
              )}
              <div>
                <Label>Reason / Notes</Label>
                <Textarea
                  value={returnNotes}
                  onChange={(e) => setReturnNotes(e.target.value)}
                  placeholder={
                    returnDialog.type === "replacement"
                      ? "Why do you need a replacement? (e.g., defective, wrong item...)"
                      : "Any notes about the return..."
                  }
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnDialog({ open: false, rental: null, type: "return" })}>
              Cancel
            </Button>
            <Button 
              onClick={handleReturnRequest} 
              disabled={submitting}
              className={returnDialog.type === "replacement" ? "bg-purple-600 hover:bg-purple-700" : ""}
            >
              {submitting ? "Submitting..." : `Submit ${returnDialog.type === "replacement" ? "Replacement" : "Return"} Request`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyRentals;
